import type { FC, ReactNode } from 'react'

interface SizeGuideProps {
    sizes: Array<string>
}

const CHEST: Record<string, [string, string]> = {
    XS: ['86–91', '68'],
    S: ['92–97', '70'],
    M: ['98–103', '72'],
    L: ['104–109', '74'],
    XL: ['110–115', '76']
}

const Table: FC<{ head: Array<string>, rows: Array<Array<string>> }> = ({ head, rows }): ReactNode => {
    return (
        <table className='w-full text-left meta-mono'>
            <thead>
                <tr className='border-b border-line text-ash'>
                    {head.map(cell => (
                        <th key={cell} scope='col' className='py-2 pr-3 font-normal'>
                            {cell}
                        </th>
                    ))}
                </tr>
            </thead>

            <tbody>
                {rows.map(row => (
                    <tr key={row[0]} className='border-b border-line text-bone'>
                        {row.map((cell, index) => (
                            <td key={index} className='py-2 pr-3'>
                                {cell}
                            </td>
                        ))}
                    </tr>
                ))}
            </tbody>
        </table>
    )
}

/** Measurements that match the kind of sizes a piece comes in: letters, waist or shoe sizes. */
const SizeGuide: FC<SizeGuideProps> = ({ sizes }): ReactNode => {
    const numeric = sizes.length > 0 && sizes.every(size => /^\d+$/.test(size))

    if (!sizes.length || (sizes.length === 1 && !numeric)) {
        return (
            <p className='body-copy text-bone/70'>
                One size, cut to sit comfortably on most frames. Unsure? Write to the studio and a pattern cutter will reply.
            </p>
        )
    }

    if (numeric && Number(sizes[0]) >= 38) {
        return (
            <Table
                head={['EU', 'UK', 'US', 'Foot (cm)']}
                rows={sizes.map(size => {
                    const eu = Number(size)

                    return [String(eu), String(eu - 34), String(eu - 33), (eu * 0.667 - 1.5).toFixed(1)]
                })}
            />
        )
    }

    if (numeric) {
        return (
            <Table
                head={['Waist', 'Waist (cm)', 'Inseam (cm)']}
                rows={sizes.map(size => [`W${size}`, String(Math.round(Number(size) * 2.54)), '81'])}
            />
        )
    }

    return (
        <Table
            head={['Size', 'Chest (cm)', 'Length (cm)']}
            rows={sizes.map(size => [size, CHEST[size]?.[0] ?? '—', CHEST[size]?.[1] ?? '—'])}
        />
    )
}

export default SizeGuide
